

















































































































































































import {
  computed,
  defineComponent,
  onBeforeMount,
  ref,
  useRoute,
  useRouter
} from '@nuxtjs/composition-api'
import { defineComponentTranslations } from '~/utils/i18n'
import { faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import {
  VehicleServiceQuickSearchCarTypeOptionGroup,
  VehicleServiceQuickSearchField,
  VehicleServiceQuickSearchForm,
  VehicleServiceRecentSearch
} from '~/models/vehicle-service'
import { useDeps } from '~/compositions/dependency-container'
import VehicleServiceService from '~/services/vehicle-service/VehicleServiceService'
import { Option } from '~/models/shared/types'
import { mapBackendOptionToOption } from '~/utils/form'
import { ciHistory } from '~/icons/source/regular/history'
import RouterService from '~/services/RouterService'
import { useI18n } from '~/compositions/i18n'
import { ciTimes } from '~/icons/source/regular/times'
import { useSnackbar } from '~/compositions/snackbar'

export default defineComponent({
  setup() {
    const [vehicleServiceService, routerService] = useDeps(
      VehicleServiceService,
      RouterService
    )
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const snackbar = useSnackbar()

    const form = ref<VehicleServiceQuickSearchForm>({
      make: route.value.query.make as string,
      model: route.value.query.model as string,
      modelCategory: route.value.query.modelCategory as string,
      generation: route.value.query.generation as string
    })

    const makesOptions = ref<Option[]>([])
    const modelsOptions = ref<Option[]>([])
    const categoryOptions = ref<Option[]>([])
    const generationOptions = ref<Option[]>([])
    const carTypeOptionGroups = ref<
      VehicleServiceQuickSearchCarTypeOptionGroup[]
    >([])
    const selectedCarType = ref(route.value.query.carType || null)
    const selectedFourWheel = ref<boolean | null>(
      route.value.query.fourWheel ? Boolean(route.value.query.fourWheel) : null
    )
    const recentSearches = ref<VehicleServiceRecentSearch[]>([])
    const loading = ref(true)
    const loadingRecentSearches = ref(false)
    const showRecentSearchesModal = ref(false)

    onBeforeMount(() => {
      loadData()
    })

    async function loadData() {
      loading.value = true

      const {
        makes,
        models,
        categories,
        generations,
        carTypes
      } = await vehicleServiceService.getQuickSearchForm(form.value)

      makesOptions.value = makes.options.map(mapBackendOptionToOption)
      form.value.make = makes.selected

      modelsOptions.value = models.options.map(mapBackendOptionToOption)
      form.value.model = getDefaultFieldValue(models)

      categoryOptions.value = categories.options.map(mapBackendOptionToOption)
      form.value.modelCategory = getDefaultFieldValue(categories)

      generationOptions.value = generations.options.map(
        mapBackendOptionToOption
      )
      form.value.generation = generations.selected
      carTypeOptionGroups.value = carTypes.options

      loading.value = false
    }

    function getDefaultFieldValue(field: VehicleServiceQuickSearchField) {
      return field.options.length === 1
        ? field.options[0].value
        : field.selected
    }

    function onMakeChange() {
      form.value.generation = null
      form.value.modelCategory = null
      form.value.model = null
      selectedCarType.value = null
      selectedFourWheel.value = null
      updateQuery()
      loadData()
    }

    function onModelChange() {
      form.value.generation = null
      form.value.modelCategory = null
      selectedCarType.value = null
      selectedFourWheel.value = null
      updateQuery()
      loadData()
    }

    function onCategoryChange() {
      form.value.generation = null
      selectedCarType.value = null
      selectedFourWheel.value = null
      updateQuery()
      loadData()
    }

    function onGenerationChange() {
      selectedCarType.value = null
      selectedFourWheel.value = null
      updateQuery()
      loadData()
    }

    function onFourWheelChange() {
      selectedCarType.value = null
      updateQuery()
    }

    function onSelectedCarTypeChange() {
      updateQuery()
    }

    function updateQuery() {
      routerService.replaceQueryParams({
        ...form.value,
        fourWheel:
          selectedFourWheel.value === null
            ? null
            : selectedFourWheel.value === true
            ? '1'
            : '0',
        carType: selectedCarType.value
      })
    }

    function clearFilters() {
      form.value.make = null
      form.value.model = null
      form.value.modelCategory = null
      form.value.generation = null
      selectedFourWheel.value = null
      selectedCarType.value = null
      updateQuery()
      loadData()
    }

    async function onCtaClick() {
      if (!selectedCarType.value) {
        snackbar.error(t('select_all_fields'))
        return
      }

      loading.value = true

      const selectedSearchInfo = carTypeOptionGroups.value
        .map(group => group.options)
        .flat()
        .find(option => option.value === selectedCarType.value)!.searchInfo

      const {
        make,
        model,
        generation,
        modelCategory,
        carId
      } = selectedSearchInfo

      try {
        await vehicleServiceService.addSearchToRecentSearches({
          make,
          model,
          generation,
          modelCategory,
          carId
        })
      } catch (error) {
        // Failed to save to recent searches, ignore error
      }

      // TODO: Consider moving this to a button href instead of programmatic navigation
      router.push({
        name: '__vehicle_service_services',
        query: selectedSearchInfo
      })
    }

    async function onRecentSearchesButtonClick() {
      loadingRecentSearches.value = true
      showRecentSearchesModal.value = true

      try {
        const { searches } = await vehicleServiceService.getRecentSearches()
        recentSearches.value = searches
      } catch (error) {
        // Failed to get recent searches, ignore error
      } finally {
        loadingRecentSearches.value = false
      }
    }

    const isFirstCarTypeOption4x4 = computed(() => {
      if (!carTypeOptionGroups.value.length) {
        return false
      }

      const [firstOption] = carTypeOptionGroups.value[0].options
      return firstOption.searchInfo.driveType4x4 === '1'
    })

    const fourWheelSelectDisabled = computed(() => {
      if (!carTypeOptionGroups.value.length) {
        return true
      }

      const firstOptionValue = isFirstCarTypeOption4x4.value ? '1' : '0'

      const areAllOptionsSameAsFirst = carTypeOptionGroups.value.every(group =>
        group.options.every(
          option => option.searchInfo.driveType4x4 === firstOptionValue
        )
      )

      // If all options are the same as the first one, we don't need to select 4x4
      return areAllOptionsSameAsFirst
    })

    const fourWheelSelectPlaceholder = computed(() => {
      if (!carTypeOptionGroups.value.length) {
        return ''
      }

      if (fourWheelSelectDisabled.value) {
        return isFirstCarTypeOption4x4.value ? t('yes') : t('no')
      }

      return ''
    })

    const filteredCarTypeOptionGroups = computed(() => {
      if (
        selectedFourWheel.value === null ||
        !carTypeOptionGroups.value.length
      ) {
        return carTypeOptionGroups.value
      }

      return carTypeOptionGroups.value
        .map(group => ({
          ...group,
          options: group.options.filter(
            option =>
              option.searchInfo.driveType4x4 ===
              (selectedFourWheel.value ? '1' : '0')
          )
        }))
        .filter(group => group.options.length)
    })

    const showClearFiltersButton = computed(() => {
      return (
        form.value.make ||
        form.value.model ||
        form.value.modelCategory ||
        form.value.generation ||
        selectedCarType.value
      )
    })

    const generationNotSupported = computed(
      () => !!form.value.generation && !carTypeOptionGroups.value.length
    )

    return {
      onMakeChange,
      onModelChange,
      onCategoryChange,
      onGenerationChange,
      onFourWheelChange,
      onSelectedCarTypeChange,
      onCtaClick,
      onRecentSearchesButtonClick,
      clearFilters,
      form,
      fourWheelSelectDisabled,
      loading,
      selectedCarType,
      makesOptions,
      modelsOptions,
      categoryOptions,
      generationOptions,
      carTypeOptionGroups,
      fourWheelSelectPlaceholder,
      selectedFourWheel,
      filteredCarTypeOptionGroups,
      recentSearches,
      loadingRecentSearches,
      showRecentSearchesModal,
      showClearFiltersButton,
      generationNotSupported,
      ciTimes,
      faTimesCircle,
      faTimes,
      ciHistory
    }
  },
  i18n: defineComponentTranslations({
    alert_message: {
      en:
        'The car service feature is only available for Athens and Thessaloniki.',
      el:
        'Η λειτουργία Service αυτοκινήτων είναι διαθέσιμη μόνο για Αθήνα και Θεσσαλονίκη.'
    },
    select_car: {
      en:
        'Select your car to see offers immediately. Available only for Athens and Thessaloniki.',
      el:
        'Επίλεξε το αυτοκίνητό σου για να δεις αμέσως προσφορές. Διαθέσιμο μόνο για Αθήνα και Θεσσαλονίκη.'
    },
    select_from_list: {
      en: 'Select from the list',
      el: 'Επιλογή από λίστα'
    },
    next_step: {
      en: 'Next step',
      el: 'Επόμενο βήμα'
    },
    no_recent_searches: {
      en: 'No recent searches found',
      el: 'Δεν βρέθηκαν πρόσφατες αναζητήσεις'
    },
    four_wheel: {
      en: '4x4 Drive',
      el: 'Κίνηση 4x4'
    },
    select_all_fields: {
      en: 'Select all fields to continue',
      el: 'Επίλεξε όλα τα πεδία για να συνεχίσεις'
    },
    generation_not_supported: {
      en: 'The model of this year is not supported yet. Try another model.',
      el:
        'Το μοντέλο αυτής της χρονολογίας δεν υποστηρίζεται ακόμα. Δοκίμασε κάποιο άλλο μοντέλο.'
    }
  })
})
